import { useCallback, useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAtomValue } from "jotai";
import { Input } from "@common/Form/Input";
import { Select } from "@common/Form/Select";
import { Button } from "@common/Button";
import { ComponentColor, ComponentSize, LabelPlacement } from "@common/types";
import { useCategoryApi } from "@/hooks/apiHooks/useCategoryApi";
import {
    categoriesAtom,
    isProcessingCategoriesAtom,
} from "@/store/categoryAtoms";
import type { CategoryResponse } from "@models/category";

const categorySchema = z.object({
    name: z.string().nonempty("Category name is required"),
    parentId: z.string().optional(),
});

type CategoryFormData = z.infer<typeof categorySchema>;

export const NewCategory = () => {
    const { createCategory } = useCategoryApi();
    const categories = useAtomValue(categoriesAtom);
    const isProcessing = useAtomValue(isProcessingCategoriesAtom);

    const methods = useForm<CategoryFormData>({
        resolver: zodResolver(categorySchema),
        defaultValues: {
            name: "",
            parentId: "0",
        },
    });

    const flattenCategories = (
        cats: CategoryResponse[],
        level = 0,
    ): Array<{ value: string; label: string }> => {
        const result: Array<{ value: string; label: string }> = [];
        cats.forEach((cat) => {
            result.push({
                value: cat.id.toString(),
                label: `${"─ ".repeat(level)}${cat.name}`,
            });

            if (cat.children?.length) {
                result.push(...flattenCategories(cat.children, level + 1));
            }
        });
        return result;
    };

    const categoryOptions = [
        { value: "0", label: "None (Root Category)" },
        ...flattenCategories(categories),
    ];

    const handleClose = useCallback(() => {
        methods.reset();

        const modal = document.getElementById(
            "newCategoryModal",
        ) as HTMLDialogElement;

        modal?.close();
    }, [methods]);

    useEffect(() => {
        if (!isProcessing && methods.formState.isSubmitSuccessful) {
            handleClose();
        }
    }, [handleClose, isProcessing, methods.formState.isSubmitSuccessful]);

    const onSubmit = async (data: CategoryFormData) => {
        await createCategory({
            name: data.name.trim(),
            parentId:
                data.parentId === "0" ? undefined : parseInt(data.parentId!),
        });
    };

    return (
        <dialog
            id="newCategoryModal"
            className="modal text-neutral"
            onCancel={(e) => {
                if (isProcessing) {
                    e.preventDefault();
                }
            }}
        >
            <div className="modal-box max-w-2xl">
                <h1 className="font-bold uppercase text-3xl">New Category</h1>
                <div className="divider divider-info mt-0"></div>
                <FormProvider {...methods}>
                    <form onSubmit={methods.handleSubmit(onSubmit)}>
                        <div className="space-y-4">
                            <Input
                                name="name"
                                placeholder="Category Name"
                                size={ComponentSize.SM}
                                labelPlacement={LabelPlacement.Inside}
                                labelText="Category Name"
                                fullWidth={true}
                                inputClass="text-black"
                            />
                            <Select
                                name="parentId"
                                options={categoryOptions}
                                size={ComponentSize.SM}
                                labelPlacement={LabelPlacement.Inside}
                                labelText="Parent Category"
                                fullWidth={true}
                                inputClass="text-black"
                            />
                        </div>
                        <div className="modal-action">
                            <Button
                                color={ComponentColor.Primary}
                                type="submit"
                                loading={isProcessing}
                            >
                                {isProcessing ? "Saving..." : "Save"}
                            </Button>
                            <Button
                                color={ComponentColor.Neutral}
                                loading={isProcessing}
                                onClick={handleClose}
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                </FormProvider>
            </div>
            {!isProcessing && (
                <form method="dialog" className="modal-backdrop">
                    <button onClick={handleClose}>close</button>
                </form>
            )}
        </dialog>
    );
};
